import React, { useState, useContext, useEffect } from "react";
import Modal from "react-modal";
import { WebSocketContext } from "../../../context/WebSocketContext";
import { useAuthCode, useGenerateAuthCode } from "../../../hooks/useAuthCodes";
import { useDevices, usePendingDevices, useApproveDevice, useRejectDevice } from "../../../hooks/useDevices";
import { usePairBambulabPrinter } from "../../../hooks/usePrinterPairing";
import ErrorDialog from "../../common/ErrorDialog";
import SuccessDialog from "../../common/SuccessDialog";
import "../../../Css/AddDevice.css";
import "../../../Css/Common.css";
import PrinterDiscovery from './PrinterDiscovery.jsx'; // Import the PrinterDiscovery component
// import "../Css/Devices.css";
Modal.setAppElement("#root"); // Set the root element for accessibility

function AddDeviceDialog({ isOpen, onClose }) {
  // Backend URL for API calls and asset serving
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  
  const [currentView, setCurrentView] = useState("main");
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedHub, setSelectedHub] = useState(null);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [steps, setSteps] = useState([]);
  const [accessCode, setAccessCode] = useState("");
  
  // Lokalny stan parowania - śledzi urządzenia w trakcie parowania
  const [pairingDevices, setPairingDevices] = useState(new Set());

  // Error dialog state
  const [errorDialog, setErrorDialog] = useState({
    isOpen: false,
    error: null
  });

  // Success dialog state
  const [successDialog, setSuccessDialog] = useState({
    isOpen: false,
    message: ""
  });

  const { setPendingDevices } = useContext(WebSocketContext);

  const { 
    data: authCodeData, 
    isLoading: isAuthCodeLoading
  } = useAuthCode({
    enabled: isOpen,
  });

  const generateAuthCodeMutation = useGenerateAuthCode();
  const pairBambulabPrinterMutation = usePairBambulabPrinter();

  const { 
    data: allHubs = [], 
    isLoading: isHubsLoading 
  } = useDevices({
    enabled: isOpen,
  });

  // Filtruj tylko huby które są online dla BambuLab
  const onlineHubs = React.useMemo(() => {
    return allHubs.filter(hub => hub.status === 1); // status === 1 oznacza online
  }, [allHubs]);

  const { 
    data: pendingDevicesData = [], 
    isLoading: isPendingDevicesLoading 
  } = usePendingDevices({
    enabled: isOpen && currentView === "nestlink",
    onSuccess: (data) => {
      setPendingDevices(data);
    }
  });

  const approveDeviceMutation = useApproveDevice();
  const rejectDeviceMutation = useRejectDevice();

  const generateAuthCode = () => {
    generateAuthCodeMutation.mutate(undefined, {
      onSuccess: () => {
        console.log("Auth code generated successfully");
      },
      onError: (error) => {
        console.error("Failed to generate auth code:", error);
      }
    });
  };

  useEffect(() => {
    // Usuń urządzenie z lokalnego stanu parowania gdy zostanie zatwierdzone przez WebSocket
    if (pendingDevicesData) {
      pendingDevicesData.forEach(device => {
        if (device.is_authorized && device.uuid && 
            pairingDevices.has(device.uuid)) {
          setPairingDevices(prev => {
            const newSet = new Set(prev);
            newSet.delete(device.uuid);
            return newSet;
          });
        }
      });
    }
  }, [pendingDevicesData, pairingDevices]);

  const pairDevice = (deviceUuid) => {
    // Dodaj urządzenie do lokalnego stanu parowania PRZED wysłaniem zapytania
    setPairingDevices(prev => new Set([...prev, deviceUuid]));
    
    approveDeviceMutation.mutate(deviceUuid, {
      onSuccess: (data) => {
        console.log(data.message || "Device pairing initiated successfully");
        // NIE usuwamy z pairingDevices tutaj - to będzie zrobione przez WebSocket
        // Urządzenie pozostanie w stanie "pairing" aż do potwierdzenia przez WebSocket
        
        // Ustaw timeout - jeśli urządzenie nie potwierdzi w 30 sekund, usuń z stanu parowania
        setTimeout(() => {
          setPairingDevices(prev => {
            if (prev.has(deviceUuid)) {
              console.warn(`Pairing timeout for device ${deviceUuid}`);
              const newSet = new Set(prev);
              newSet.delete(deviceUuid);
              return newSet;
            }
            return prev;
          });
        }, 30000); // 30 sekund timeout
      },
      onError: (error) => {
        console.error("Error pairing device:", error);
        // Usuń z lokalnego stanu parowania w przypadku błędu
        setPairingDevices(prev => {
          const newSet = new Set(prev);
          newSet.delete(deviceUuid);
          return newSet;
        });
        alert(`Failed to pair device: ${error.message}`);
      }
    });
  };

  const rejectDevice = (deviceUuid) => {
    rejectDeviceMutation.mutate(deviceUuid, {
      onSuccess: (data) => {
        console.log(data.message || "Device rejected successfully");
      },
      onError: (error) => {
        console.error("Error rejecting device:", error);
      }
    });
  };

  const handleDeviceTypeSelect = (type) => {
    if (["bambulab", "octoprint", "prusa"].includes(type)) {
      setSteps(stepsConfig.printers[type]);
      setCurrentStep(1);
      setCurrentView(type);
    } else if (type === "nestlink") {
      setSteps(stepsConfig.other.nestlink);
      setCurrentStep(1);
      setCurrentView("nestlink");
    }
  };

  // Funkcja obsługująca parowanie drukarki BambuLab
  const handlePairPrinter = async () => {
    if (!selectedPrinter || !selectedHub || !accessCode.trim()) {
      alert("Wszystkie pola są wymagane");
      return;
    }

    // Blokuj parowanie cloud drukarek
    if (selectedPrinter.connection_type === 'cloud') {
      setErrorDialog({
        isOpen: true,
        error: {
          message: "Nie można sparować drukarek Cloud. Obsługujemy tylko drukarki LAN.",
          response: { status: 400 }
        }
      });
      return;
    }

    try {
      const printerData = {
        serial_number: selectedPrinter.serialNumber,
        access_code: accessCode.trim()
      };

      await pairBambulabPrinterMutation.mutateAsync({
        hubUuid: selectedHub,
        printerData: printerData
      });

      // Pokaż success dialog
      setSuccessDialog({
        isOpen: true,
        message: `Drukarka ${selectedPrinter.device_name} została pomyślnie sparowana z hubem!`
      });
      
      // Auto close modal after success
      setTimeout(() => {
        handleModalClose();
      }, 3500);
      
    } catch (error) {
      // Log tylko podstawowe informacje o błędzie
      console.error("Printer pairing failed:", error.message);
      
      // Pokaż error dialog zamiast alert
      setErrorDialog({
        isOpen: true,
        error: error
      });
    }
  };

  const handleNext = () => {
    // Jeśli aktualny widok to "printer-selection" i drukarka jest cloud, blokuj przejście
    if (currentView === "printer-selection" && selectedPrinter?.connection_type === 'cloud') {
      setErrorDialog({
        isOpen: true,
        error: {
          message: "Nie można kontynuować z drukarką Cloud. Wybierz drukarkę LAN.",
          response: { status: 400 }
        }
      });
      return;
    }
    
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setCurrentView(steps[currentStep].view);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setCurrentView(steps[currentStep - 2].view);
    } else {
      setCurrentView("main");
      setSteps([]);
      setCurrentStep(1);
      setSelectedHub(null);
      setSelectedPrinter(null);
    }
  };

  const handlePrinterSelected = (printer) => {
    setSelectedPrinter(printer);
    // Automatycznie przejdź do następnego kroku (wprowadzania kodu)
    handleNext();
  };

  const stepsConfig = {
    printers: {
      bambulab: [
        { label: "Select Hub", view: "bambulab" },
        { label: "Select device", view: "selectDevice" },
        { label: "Enter code", view: "enterCode" },
      ],
      prusa: [
        { label: "Select Hub", view: "prusa" },
        { label: "Select device", view: "selectDevice" },
        { label: "Enter code", view: "enterCode" },
      ],
      octoprint: [
        { label: "Select Hub", view: "octoprint" },
        { label: "Select device", view: "selectDevice" },
        { label: "Enter code", view: "enterCode" },
      ],
    },
    other: {
      nestlink: [
        { label: "Auth code", view: "nestlink" },
        { label: "Pending devices", view: "pendingDevices" },
      ],
    },
  };

  const formatExpiryTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString();
  };

  // Funkcja resetowania stanu modala
  const resetModalState = () => {
    setCurrentView("main");
    setCurrentStep(1);
    setSelectedHub(null);
    setSelectedPrinter(null);
    setSteps([]);
    setAccessCode("");
    setPairingDevices(new Set());
    setErrorDialog({ isOpen: false, error: null });
    setSuccessDialog({ isOpen: false, message: "" });
  };

  // Nadpisz funkcję onClose z resetowaniem
  const handleModalClose = () => {
    resetModalState();
    setErrorDialog({ isOpen: false, error: null });
    setSuccessDialog({ isOpen: false, message: "" });
    onClose();
  };

  // Funkcja do zamykania error dialog
  const handleErrorDialogClose = () => {
    setErrorDialog({ isOpen: false, error: null });
  };

  // Funkcja do zamykania success dialog
  const handleSuccessDialogClose = () => {
    setSuccessDialog({ isOpen: false, message: "" });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleModalClose}
      contentLabel="Add Device"
      className="modal"
      overlayClassName="overlay"
    >
      <div className="flex modal-box">
        {steps.length > 0 && (
          <div>
            <ol style={{ listStyle: "none", padding: 0 }}>
              {steps.map((step, idx) => (
                <li key={step.label} style={{ marginBottom: 16 }}>
                  <span
                    style={{
                      display: "block",
                      fontWeight: currentStep === idx + 1 ? "bold" : "normal",
                      color: currentStep === idx + 1 ? "#007bff" : "#888",
                      fontSize: 16,
                      cursor: "default",
                      padding: 0,
                    }}
                  >
                    {idx + 1}. {step.label}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div style={{ flex: 1 }}>
          <div className="modal-top flex">
            <h1>Add Device</h1>
            <div>
              <button className="btn-close" onClick={handleModalClose} aria-label="Close">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: "block" }}
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div className="modal-content">
            {currentView === "main" && (
              <>
                <section className="device-buttons">
                  <button className="device-button" onClick={() => handleDeviceTypeSelect("bambulab")}>
                    <img src={`${API_URL}/api/icons/platforms/bambulab/preview.webp`} alt="BambuLab" className="device-icon" />
                  </button>
                  <button className="device-button" onClick={() => handleDeviceTypeSelect("octoprint")}>
                    <img src={`${API_URL}/api/icons/platforms/octoprint/preview.webp`} alt="OctoPrint" className="device-icon" />
                  </button>
                  <button className="device-button" onClick={() => handleDeviceTypeSelect("prusa")}>
                    <img src={`${API_URL}/api/icons/platforms/prusa/preview.webp`} alt="Prusa" className="device-icon" />
                  </button>
                  <button className="device-button" onClick={() => handleDeviceTypeSelect("nestlink")}>
                    <img src={`${API_URL}/api/icons/platforms/nestlinker/preview.webp`} alt="NestLink" className="device-icon" />
                  </button>
                </section>
              </>
            )}

            {currentView === "bambulab" && (
              <>
                <section className="bambulab-view">
                  {currentStep > 1 && (
                    <button onClick={handlePrev} className="back-button">
                      Wstecz
                    </button>
                  )}
                  <h2>Your Online Hubs</h2>
                  <div className="hub-buttons">
                    {isHubsLoading ? (
                      <p>Loading hubs...</p>
                    ) : onlineHubs.length === 0 ? (
                      <div className="no-hubs-message">
                        <p>No online hubs found.</p>
                        <p>Make sure your NestHub devices are connected and online.</p>
                      </div>
                    ) : (
                      onlineHubs.map((hub) => (
                        <button
                          key={hub.uuid}
                          className="hub-button"
                          onClick={() => {
                            setSelectedHub(hub.uuid);
                            handleNext();
                          }}
                        >
                          <div className="avatar-cell">
                            {hub.thumb}
                          </div>
                          <p><strong>{hub.name}</strong></p>
                          <p>Version: {hub.version}</p>
                          <p>OS: {hub.os}</p>
                          <div className="hub-status online">
                            <span>● Online</span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </section>
              </>
            )}

            {(currentView === "octoprint" || currentView === "prusa") && (
              <>
                <section className={`${currentView}-view`}>
                  {currentStep > 1 && (
                    <button onClick={handlePrev} className="back-button" style={{ marginRight: 12 }}>
                      Wstecz
                    </button>
                  )}
                  <h2>Your Hubs</h2>
                </section>
              </>
            )}

            {currentView === "nestlink" && (
              <>
                <section className="auth-code">
                  <div>
                    {isAuthCodeLoading ? (
                      <p>Loading auth code...</p>
                    ) : authCodeData?.auth_code ? (
                      <div>
                        <p>
                          Your auth code: <strong>{authCodeData.auth_code}</strong>
                        </p>
                        <p>Valid until: {formatExpiryTime(authCodeData.expires_at)}</p>
                      </div>
                    ) : (
                      <p>No active auth code found.</p>
                    )}
                    <button 
                      onClick={generateAuthCode}
                      disabled={generateAuthCodeMutation.isPending}
                    >
                      {generateAuthCodeMutation.isPending 
                        ? 'Generating...' 
                        : 'Generate Auth Code'}
                    </button>
                  </div>
                </section>

                <section className="pending-devices">
                  <h2>Pending Devices</h2>
                  {isPendingDevicesLoading ? (
                    <p>Loading pending devices...</p>
                  ) : pendingDevicesData.length === 0 ? (
                    <p>No devices pending approval.</p>
                  ) : (
                    pendingDevicesData.map((device) => (
                      <div key={device.uuid} className="device-item">
                        <p>
                          <strong>Device Type:</strong> {device.device_type}
                        </p>
                        <p>
                          <strong>Version:</strong> {device.device_version}
                        </p>
                        <p>
                          <strong>OS:</strong> {device.device_os || "N/A"}
                        </p>
                        <button 
                          onClick={() => pairDevice(device.uuid)}
                          disabled={pairingDevices.has(device.uuid) || device.is_authorized || approveDeviceMutation.isPending}
                          className={`pair-button ${
                            pairingDevices.has(device.uuid) || (approveDeviceMutation.isPending && approveDeviceMutation.variables === device.uuid) 
                              ? 'pairing' 
                              : device.is_authorized 
                                ? 'paired' 
                                : ''
                          }`}
                        >
                          {device.is_authorized 
                            ? 'Paired ✓'
                            : pairingDevices.has(device.uuid) || (approveDeviceMutation.isPending && approveDeviceMutation.variables === device.uuid)
                              ? 'Pairing...' 
                              : 'Pair Device'}
                        </button>
                        <button 
                          onClick={() => rejectDevice(device.uuid)}
                          disabled={rejectDeviceMutation.isPending}
                        >
                          {rejectDeviceMutation.isPending && 
                          rejectDeviceMutation.variables === device.uuid 
                            ? 'Rejecting...' 
                            : 'Reject'}
                        </button>
                        <p>
                          <strong>Created At:</strong> {new Date(device.created_at).toLocaleString()}
                        </p>
                        <hr />
                      </div>
                    ))
                  )}
                </section>
              </>
            )}

            {currentView === "selectDevice" && (
              <>
                <section className="select-device-view">
                  <h2>Select a Printer</h2>
                  {selectedHub ? (
                    <PrinterDiscovery 
                      hubUuid={selectedHub}
                      onPrinterSelected={handlePrinterSelected}
                    />
                  ) : (
                    <p className="error-message">No hub selected. Please go back and select a hub.</p>
                  )}
                </section>
              </>
            )}

            {currentView === "enterCode" && (
              <>
                <section className="enter-code-view">
                  <h2>Enter Access Code</h2>
                  {selectedPrinter ? (
                    <div className="code-entry-section">
                      <div className="selected-printer-summary">
                        <h3>Selected Printer:</h3>
                        <div className="printer-summary-card">
                          <p><strong>Name:</strong> {selectedPrinter.device_name}</p>
                          <p><strong>Model:</strong> {selectedPrinter.model}</p>
                          <p><strong>IP Address:</strong> {selectedPrinter.ip}</p>
                          <p><strong>Connection Type:</strong> 
                            <span className={`connection-type connection-${selectedPrinter.connection_type}`}>
                              {selectedPrinter.connection_type === 'cloud' ? 'Cloud' : 'LAN'}
                            </span>
                          </p>
                        </div>
                      </div>
                      
                      <div className="access-code-form">
                        <h3>Access Code</h3>
                        <p className="instruction-text">
                          Please enter the access code from your printer's display to complete the connection.
                        </p>
                        
                        <div className="code-input-container">
                          <input
                            type="text"
                            placeholder="Enter access code"
                            className="access-code-input"
                            maxLength="8"
                            value={accessCode}
                            onChange={(e) => setAccessCode(e.target.value)}
                          />
                        </div>
                        
                        <div className="code-actions">
                          <button className="btn-secondary" onClick={handlePrev}>
                            Back
                          </button>
                          <button 
                            className="btn-primary"
                            onClick={handlePairPrinter}
                            disabled={pairBambulabPrinterMutation.isPending || !accessCode.trim()}
                          >
                            {pairBambulabPrinterMutation.isPending ? 'Connecting...' : 'Connect Printer'}
                          </button>
                        </div>
                      </div>
                      
                      <div className="help-section">
                        <h4>Need help finding the access code?</h4>
                        <ol>
                          <li>Go to your printer's touchscreen</li>
                          <li>Navigate to Settings → Network → LAN</li>
                          <li>Look for "Access Code" or "Connect Code"</li>
                          <li>Enter the 8-digit code shown on screen</li>
                        </ol>
                      </div>
                    </div>
                  ) : (
                    <p className="error-message">No printer selected. Please go back and select a printer.</p>
                  )}
                </section>
              </>
            )}

            <div style={{ marginTop: 24, display: "flex", gap: 8 }}>
              {currentView !== "main" && currentView !== "enterCode" && (
                <button
                  onClick={handlePrev}
                  className="back-button"
                >
                  Wstecz
                </button>
              )}
              {currentStep < steps.length && currentView !== "main" && currentView !== "enterCode" && (
                <button 
                  onClick={handleNext}
                  className="btn-primary"
                  disabled={currentView === "printer-selection" && selectedPrinter?.connection_type === 'cloud'}
                  title={currentView === "printer-selection" && selectedPrinter?.connection_type === 'cloud' ? 
                    'Wybierz drukarkę LAN aby kontynuować' : ''}
                >
                  Dalej
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Error Dialog */}
      <ErrorDialog
        error={errorDialog.error}
        isOpen={errorDialog.isOpen}
        onClose={handleErrorDialogClose}
        title="Błąd parowania drukarki"
      />
      
      {/* Success Dialog */}
      <SuccessDialog
        message={successDialog.message}
        isOpen={successDialog.isOpen}
        onClose={handleSuccessDialogClose}
        title="Drukarka sparowana!"
        autoClose={true}
        autoCloseDelay={3000}
      />
    </Modal>
  );
}

export default AddDeviceDialog;